/**
 * Turns a tile's active conditions (dashboard filters, cross-filters from
 * other tiles, the tile's own base) into one engine universe: a QTABLE
 * of condition groups flattened into a field. Paths are OR-ed within a group
 * and groups are AND-ed. The names carry a hash of the canonical groups, so
 * tiles with the same conditions share one declaration.
 */

import type { HierarchicalDataEntry } from "types/engineOutput.types"
import { getBoolChildLabels } from "utils/codebookLeaves"
import { FilterDef } from "features/Dashboard/types/dashboardDocument"

export interface ConditionGroup {
  paths: string[]
}
export interface CrossFilterEntry {
  variable: string
  categories: string[]
}
export interface UniverseSpec {
  hash: string
  fieldName: string
  containerName: string
  /** QTABLE/APPEND/FFIELD lines, no trailing newline. */
  prefix: string
  /** " UNIVERSE: <<field>>" including the leading space. */
  clause: string
}

/**
 * The filter selections that can reach the engine: only categories the
 * codebook lists under each filter's variable. A filter whose variable is
 * gone, or a category that is no longer there (a re-imported dataset), is
 * left out rather than sent as a dead path that would fail every tile.
 * Before the first codebook nothing is known to be missing, so the
 * selections pass unchanged.
 */
export const liveFilterSelections = (
  filters: FilterDef[],
  selections: Record<string, string[]>,
  codebook: HierarchicalDataEntry[] | null
): Record<string, string[]> => {
  if (codebook === null) return selections
  return Object.fromEntries(
    filters.map((f) => {
      const known = new Set(getBoolChildLabels(codebook, f.variable))
      return [f.id, (selections[f.id] ?? []).filter((c) => known.has(c))]
    })
  )
}

/**
 * A tile's condition groups: one per dashboard filter with a selection, one
 * per other tile's cross-filter (when the tile receives them), then the
 * tile's own base, `tileConditions` (each inner array one OR group; blank
 * paths and empty groups are skipped).
 */
export const collectConditionGroups = (args: {
  filters: FilterDef[]
  filterSelections: Record<string, string[]>
  crossFilters: Record<string, CrossFilterEntry>
  tileId: string
  receives: boolean
  tileConditions?: string[][]
}): ConditionGroup[] => {
  const groups: ConditionGroup[] = []
  args.filters.forEach((f) => {
    const selected = (args.filterSelections[f.id] ?? []).filter((s) =>
      s.trim()
    )
    if (selected.length > 0) {
      groups.push({ paths: selected.map((c) => `${f.variable}//${c}`) })
    }
  })
  if (args.receives) {
    Object.entries(args.crossFilters).forEach(([emitter, entry]) => {
      if (emitter === args.tileId || entry.categories.length === 0) return
      groups.push({
        paths: entry.categories.map((c) => `${entry.variable}//${c}`),
      })
    })
  }
  args.tileConditions?.forEach((g) => {
    const paths = g.map((p) => p.trim()).filter(Boolean)
    if (paths.length) groups.push({ paths })
  })
  return groups
}

const canonical = (groups: ConditionGroup[]): string[][] =>
  groups
    .map((g) => Array.from(new Set(g.paths)).sort())
    .filter((g) => g.length > 0)
    .sort((a, b) => a.join("|").localeCompare(b.join("|")))

/** FNV-1a 32-bit over the canonical groups, 8 hex chars, order-independent. */
export const hashGroups = (groups: ConditionGroup[]): string => {
  const text = JSON.stringify(canonical(groups))
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h.toString(16).padStart(8, "0")
}

export const composeUniverse = (
  groups: ConditionGroup[]
): UniverseSpec | null => {
  const canon = canonical(groups)
  if (canon.length === 0) return null
  const hash = hashGroups(groups)
  const containerName = `__dq_c_${hash}`
  const fieldName = `__dq_f_${hash}`
  const lines = [`QTABLE ${containerName}`]
  canon.forEach((paths, i) => {
    const sub = `${containerName}::g${i + 1}`
    lines.push(`QTABLE ${sub}`)
    paths.forEach((p) => lines.push(`APPEND ${sub} <<${p}>>`))
  })
  lines.push(`FFIELD ${fieldName} ${containerName}`)
  return {
    hash,
    fieldName,
    containerName,
    prefix: lines.join("\n"),
    clause: ` UNIVERSE: <<${fieldName}>>`,
  }
}
